import { CareerProfessionsQuery } from 'core';

import {
  ISkillGap,
  ISkillProgress,
  RequirementStatus,
  toRequirementStatus,
  transformSkillProgress,
} from './transformSkillProgress';

export interface IProfessionSkill {
  id: string;
  name: string;
  slug: string;
  category: string;
  iconUrl: string | null;
  /** Навык уже есть у студента — красим и считаем в «X из Y». */
  have: boolean;
  /** ТЗ 0071: соответствует / развить / получить — та же функция, что и счётчик. */
  requirementStatus: RequirementStatus;
  /** 'core' | 'important' | 'optional' */
  priority: string;
  requiredLevel: number;
  /** `null` — навыка нет или он скрыт. */
  myLevel: number | null;
}

export interface IProfessionCourse {
  courseId: string;
  courseName: string;
  /** Страница курса на сайте школы: витрины курсов в ЛМС нет. */
  courseUrl: string;
  completed: boolean;
}

export interface IProfessionStep {
  title: string;
  description: string;
  satisfied: boolean;
  current: number;
  target: number;
  progressKind: string;
}

export interface IProfession {
  code: string;
  title: string;
  summary: string;
  about: string;
  /** Знак профессии из справочника; пустой рисуется портфелем. */
  icon: string;
  /** Вилка целиком нуллабельна: незаполненную не показываем вовсе. */
  salaryFrom: number | null;
  salaryTo: number | null;
  salaryCurrency: string;
  haveCount: number;
  totalCount: number;
  /** ТЗ 0071: те же пять чисел, что на главной, в паспорте и на карте. */
  skillProgress: ISkillProgress;
  /** Три ближайших незакрытых обязательных навыка: ключевые, затем важные. */
  topGaps: ISkillGap[];
  /** Вакансий В НАШЕЙ ЛЕНТЕ, не на рынке — экран обязан это подписать. */
  liveVacancies: number;
  skills: IProfessionSkill[];
  courses: IProfessionCourse[];
  /** Выбранная цель студента. Подставленный по умолчанию трек целью НЕ считается. */
  isChosen: boolean;
  /**
   * Методист ещё не описал целевые навыки профессии. Карточка показывается —
   * иначе её нельзя ни увидеть, ни выбрать, — но прогресс и число вакансий у
   * неё бессмысленны и гасятся по ЭТОМУ флагу, а не по нулю в знаменателе.
   */
  hasRequirements: boolean;
  steps: IProfessionStep[];
  stepsDone: number;
  stepsTotal: number;
}

export const transformProfessions = (data: CareerProfessionsQuery | undefined): IProfession[] =>
  (data?.careerProfessions ?? []).map((row) => ({
    code: row.code,
    title: row.title,
    summary: row.summary,
    about: row.about,
    icon: row.icon,
    salaryFrom: row.salaryFrom ?? null,
    salaryTo: row.salaryTo ?? null,
    salaryCurrency: row.salaryCurrency,
    haveCount: row.haveCount,
    totalCount: row.totalCount,
    skillProgress: transformSkillProgress(row.skillProgress),
    topGaps: row.topGaps.map((gap) => ({
      slug: gap.slug,
      name: gap.name,
      status: toRequirementStatus(gap.status),
      priority: gap.priority,
      requiredLevel: gap.requiredLevel,
      myLevel: gap.myLevel ?? null,
    })),
    liveVacancies: row.liveVacancies,
    isChosen: row.isChosen,
    hasRequirements: row.hasRequirements,
    stepsDone: row.stepsDone,
    stepsTotal: row.stepsTotal,
    steps: row.steps.map((step) => ({
      title: step.title,
      description: step.description,
      satisfied: step.satisfied,
      current: step.current,
      target: step.target,
      progressKind: step.progressKind,
    })),
    skills: row.skills.map((skill) => ({
      id: skill.id,
      name: skill.name,
      slug: skill.slug,
      category: skill.category,
      iconUrl: skill.iconUrl ?? null,
      have: skill.have,
      requirementStatus: toRequirementStatus(skill.requirementStatus),
      priority: skill.priority,
      requiredLevel: skill.requiredLevel,
      myLevel: skill.myLevel ?? null,
    })),
    courses: row.courses.map((course) => ({
      courseId: course.courseId,
      courseName: course.courseName,
      courseUrl: course.courseUrl,
      completed: course.completed,
    })),
  }));
